import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const formatTypeLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const CTATool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [ctaDirection, setCtaDirection] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [ctas, setCtas] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
      outputBg: isDark ? "rgba(2,6,23,0.28)" : "#ffffff",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setCtas([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setSharedPlatform("YouTube");
    setAudience("");
    setGoal("");
    setOffer("");
    setPainPoint("");
    setDesiredOutcome("");
    setBrandVoice("");
    setCtaDirection("");
    setAvoidPhrases("");
    setExtraContext("");
    setCtas([]);
    setCopiedId(null);
    setError("");
  };

  const generateCTAs = async () => {
    if (!sharedTopic.trim()) {
      setError(t("studioTools.ctaTool.errors.topicRequired"));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/ctas", {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        audience,
        goal,
        offer,
        pain_point: painPoint,
        desired_outcome: desiredOutcome,
        brand_voice: brandVoice,
        call_to_action: ctaDirection,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setCtas(Array.isArray(response?.data?.ctas) ? response.data.ctas : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate CTAs:", err);
      setError(
        err?.response?.data?.detail || t("studioTools.ctaTool.errors.generateFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const copyCTA = async (cta, id) => {
    const textToCopy = [
      cta?.text || "",
      cta?.why ? `${t("studioTools.ctaTool.output.whyItWorks")}: ${cta.why}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy CTA:", err);
    }
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("studioTools.ctaTool.hero.badge")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("studioTools.ctaTool.hero.title")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t("studioTools.ctaTool.hero.descriptionStart")}{" "}
          <strong>{t("studioTools.ctaTool.hero.descriptionStrong")}</strong>{" "}
          {t("studioTools.ctaTool.hero.descriptionEnd")}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("studioTools.ctaTool.fields.topic.label")}
              <FieldHint text={t("studioTools.ctaTool.fields.topic.hint")} />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("studioTools.ctaTool.fields.topic.placeholder")}
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("studioTools.ctaTool.fields.platform.label")}
                <FieldHint text={t("studioTools.ctaTool.fields.platform.hint")} />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "YouTube",
                  "YouTube Shorts",
                  "Instagram",
                  "Instagram Reels",
                  "TikTok",
                  "X",
                  "LinkedIn",
                  "Facebook",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("studioTools.ctaTool.fields.audience.label")}
                <FieldHint text={t("studioTools.ctaTool.fields.audience.hint")} />
              </label>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder={t("studioTools.ctaTool.fields.audience.placeholder")}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("studioTools.ctaTool.context.title")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.goal.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.goal.hint")} />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.goal.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.offer.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.offer.hint")} />
                </label>
                <input
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.offer.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.painPoint.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.painPoint.hint")} />
                </label>
                <input
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.painPoint.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.desiredOutcome.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.desiredOutcome.hint")} />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.desiredOutcome.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.brandVoice.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.brandVoice.hint")} />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.brandVoice.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.ctaDirection.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.ctaDirection.hint")} />
                </label>
                <input
                  value={ctaDirection}
                  onChange={(e) => setCtaDirection(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.ctaDirection.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.ctaTool.fields.avoidPhrases.label")}
                  <FieldHint text={t("studioTools.ctaTool.fields.avoidPhrases.hint")} />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t("studioTools.ctaTool.fields.avoidPhrases.placeholder")}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("studioTools.ctaTool.fields.extraContext.label")}
                <FieldHint text={t("studioTools.ctaTool.fields.extraContext.hint")} />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t("studioTools.ctaTool.fields.extraContext.placeholder")}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateCTAs}
              disabled={loading}
            >
              {loading
                ? t("studioTools.ctaTool.actions.generating")
                : t("studioTools.ctaTool.actions.generate")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("studioTools.ctaTool.actions.clearResults")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("studioTools.ctaTool.actions.resetForm")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!ctas.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t("studioTools.ctaTool.resultsIntro.start")}{" "}
          <strong>{t("studioTools.ctaTool.resultsIntro.strong")}</strong>.{" "}
          {t("studioTools.ctaTool.resultsIntro.end")}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        ctas.map((cta, index) => {
          const scoreTone = getScoreTone(cta.score);

          return (
            <ResultCard
              key={`${cta.cta_type}-${index}`}
              colors={colors}
              index={index}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginBottom: "12px",
                }}
              >
                {cta.cta_type && (
                  <span
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.chipText,
                      fontWeight: 700,
                    }}
                  >
                    {formatTypeLabel(cta.cta_type)}
                  </span>
                )}

                <span
                  style={{
                    background: scoreTone.pillBg,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    color: scoreTone.pillText,
                    fontWeight: 700,
                  }}
                >
                  {t("studioTools.ctaTool.output.score")}: {cta.score}
                </span>

                {cta.label && (
                  <span
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.chipText,
                      fontWeight: 700,
                    }}
                  >
                    {cta.label}
                  </span>
                )}
              </div>

              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  background: colors.outputBg,
                  borderRadius: "14px",
                  padding: "14px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    color: colors.text,
                    lineHeight: "1.7",
                    fontWeight: 700,
                    fontSize: "16px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {cta.text}
                </div>
              </div>

              {cta.why && (
                <div
                  style={{
                    color: colors.textMuted,
                    lineHeight: "1.65",
                    marginBottom: "12px",
                  }}
                >
                  <strong style={{ color: colors.text }}>
                    {t("studioTools.ctaTool.output.whyItWorks")}:
                  </strong>{" "}
                  {cta.why}
                </div>
              )}

              <ActionRow>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyCTA(cta, index)}
                >
                  {copiedId === index
                    ? t("studioTools.ctaTool.actions.copied")
                    : t("studioTools.ctaTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="cta_generator"
                  title={`CTA • ${sharedTopic || t("studioTools.ctaTool.output.untitledTopic")}`}
                  topic={sharedTopic}
                  platform={sharedPlatform}
                  content={[cta.text || "", cta.why ? `${t("studioTools.ctaTool.output.whyItWorks")}: ${cta.why}` : ""]
                    .filter(Boolean)
                    .join("\n\n")}
                  meta={{
                    score: cta.score,
                    label: cta.label,
                    cta_type: cta.cta_type || "",
                  }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default CTATool;